import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Clock, CheckCircle, TrendingUp, TrendingDown,
    AlertTriangle, ArrowRight,
    PieChart, DollarSign, Activity, ListTodo,
    Building2, BookOpen, Users as UsersIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { VisitCalendar, DetailedTimeline, ProDistributionChart } from './DashboardWidgets';
import TaskActionList from './TaskActionList';
import DashboardSkeleton from './DashboardSkeleton';

/* --- SUB-COMPONENTS (WIDGETS) --- */

const StatCard = ({ title, value, trend, trendValue, icon: Icon, color, lightColor, onClick, isClickable = true }) => (
    <Card
        className={`border-l-4 shadow-sm transition-all dark:bg-slate-900 dark:border-slate-800 ${isClickable ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]' : ''}`}
        style={{ borderLeftColor: color }}
        onClick={isClickable ? onClick : undefined}
    >
        <CardContent className="p-5">
            <div className="flex justify-between items-start">
                <div>
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{title}</p>
                    <h3 className="text-2xl font-black text-slate-900 dark:text-white mt-1">{value}</h3>
                </div>
                <div className="p-2.5 rounded-xl transition-colors" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-5 h-5" style={{ color: color }} />
                </div>
            </div>
            <div className="flex items-center mt-3 gap-2">
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded flex items-center ${trend === 'up' ? 'text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400' : 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-400'}`}>
                    {trend === 'up' ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                    {trendValue}
                </span>
                <span className="text-[10px] text-slate-400 font-medium">vs semana anterior</span>
            </div>
            {isClickable && (
                <div className="mt-2 text-[10px] text-blue-600 dark:text-blue-400 font-bold flex items-center gap-1">
                    Ver detalles <ArrowRight className="w-3 h-3" />
                </div>
            )}
        </CardContent>
    </Card>
);

const TodoList = () => (
    <Card className="h-full shadow-sm border-0 bg-white dark:bg-slate-900 shadow-xl shadow-blue-900/5">
        <CardHeader className="pb-2 border-b border-slate-50 dark:border-slate-800 flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-white">
                <ListTodo className="w-5 h-5 text-indigo-500" />
                Tareas Pendientes
            </CardTitle>
            <Badge variant="secondary" className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border-0 h-5 px-2 text-[10px] font-bold">4 Pendientes</Badge>
        </CardHeader>
        <CardContent className="pt-4 p-4">
            <div className="space-y-2">
                {[
                    { text: "Enviar ficha técnica a Mariana", urgent: true, done: false },
                    { text: "Llamar a Notaría por escritura #405", urgent: true, done: false },
                    { text: "Actualizar fotos de Casa Lirios", urgent: false, done: false },
                    { text: "Seguimiento Lead: Roberto Mendez", urgent: false, done: false },
                ].map((task, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border border-transparent hover:border-slate-100 dark:hover:border-slate-800 group">
                        <div className={cn(
                            "w-5 h-5 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all",
                            task.urgent ? 'border-red-200 dark:border-red-900' : 'border-slate-200 dark:border-slate-700'
                        )}>
                            <div className="w-2.5 h-2.5 rounded-full bg-transparent group-hover:bg-slate-300 dark:group-hover:bg-slate-600"></div>
                        </div>
                        <span className={cn(
                            "text-xs flex-1",
                            task.urgent ? 'font-bold text-slate-900 dark:text-slate-200' : 'text-slate-600 dark:text-slate-400'
                        )}>{task.text}</span>
                        {task.urgent && <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></div>}
                    </div>
                ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-50 dark:border-slate-800">
                <Button className="w-full bg-slate-900 dark:bg-blue-600 text-white hover:bg-black dark:hover:bg-blue-700 h-9 text-xs font-bold rounded-xl shadow-lg shadow-blue-900/20">
                    + Nueva Tarea
                </Button>
            </div>
        </CardContent>
    </Card>
);

export default function AgentDashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [stats, setStats] = React.useState({
        properties: 0,
        missingImages: 0,
        leads: 0,
        conversion: '4.2%',
        responseTime: '15m'
    });
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        const fetchDashboardData = async (retries = 3) => {
            if (!user?.id) return;
            setError(null);
            try {
                // Use the optimized summary endpoint with a timeout helper
                const fetchWithTimeout = async (url, options = {}) => {
                    const { timeout = 5000 } = options;
                    const controller = new AbortController();
                    const id = setTimeout(() => controller.abort(), timeout);
                    try {
                        const response = await fetch(url, { ...options, signal: controller.signal });
                        clearTimeout(id);
                        return response;
                    } catch (e) {
                        clearTimeout(id);
                        throw e;
                    }
                };

                const response = await fetchWithTimeout('/api.php?action=dashboard_summary');
                if (!response.ok) throw new Error('Error al conectar con la API');

                const data = await response.json();
                if (data.success) {
                    setStats(prev => ({
                        ...prev,
                        properties: data.active_properties,
                        leads: data.total_leads,
                        inventory_value: data.inventory_value,
                        pending_tasks: data.pending_tasks
                    }));
                } else {
                    throw new Error(data.message || 'Error al cargar datos');
                }
            } catch (error) {
                console.error("Error fetching dashboard stats:", error);
                if (retries > 0) {
                    console.log(`Retrying... (${retries} left)`);
                    setTimeout(() => fetchDashboardData(retries - 1), 1000);
                } else {
                    setError("No se pudieron cargar las métricas. Por favor, recarga la página.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [user]);

    if (loading) return <DashboardSkeleton />;

    return (
        <div className="p-6 md:p-8 space-y-8 max-w-[1600px] mx-auto animate-in fade-in duration-500 bg-slate-50/30 dark:bg-transparent">
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-2 font-medium">
                        <AlertTriangle className="w-5 h-5" />
                        {error}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => fetchDashboardData()} className="text-red-700 hover:bg-red-100">
                        Reintentar
                    </Button>
                </div>
            )}
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                <div className="space-y-1">
                    <h1 className="text-4xl font-black tracking-tight text-slate-900 dark:text-white">Panel de Agente</h1>
                    <p className="text-slate-500 font-medium">Bienvenido de nuevo. Aquí está el resumen de tu negocio inmobiliario.</p>
                </div>
                <div className="flex flex-wrap gap-3">
                    <Button variant="outline" className="h-11 px-6 gap-2 font-bold rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200" onClick={() => navigate(`/properties`)}>
                        <Building2 className="w-4 h-4" /> Mis Propiedades
                    </Button>
                    <Button className="h-11 px-8 gap-2 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-xl shadow-xl shadow-blue-500/20" onClick={() => navigate('/properties/create')}>
                        + Publicar Inmueble
                    </Button>
                </div>
            </div>

            {/* Top Alert Bar */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-400 dark:from-orange-600 dark:to-orange-500 rounded-2xl p-4 shadow-xl shadow-orange-500/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-white">
                <div className="flex items-center gap-4">
                    <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-md">
                        <Building2 className="w-6 h-6" />
                    </div>
                    <div>
                        <h4 className="font-black text-sm uppercase tracking-tight">Estado del Catálogo</h4>
                        <p className="text-xs text-white/90 font-medium">
                            {stats.properties === 0
                                ? "Aún no tienes propiedades publicadas. Empieza hoy para atraer clientes."
                                : stats.missingImages > 0
                                    ? `Tienes ${stats.missingImages} propiedades sin imágenes. Completa tu catálogo para aumentar leads.`
                                    : "¡Excelente! Todas tus propiedades tienen imágenes y están listas para vender."}
                        </p>
                    </div>
                </div>
                <Button
                    className="bg-white text-orange-600 font-black hover:bg-white/90 rounded-xl h-9 px-6 text-xs transition-transform active:scale-95 shadow-lg"
                    onClick={() => navigate('/properties/create')}
                >
                    {stats.properties === 0 ? "Publicar Ahora" : (stats.missingImages > 0 ? "Solucionar Ahora" : "+ Publicar")}
                </Button>
            </div>

            {/* KPIs Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Propiedades Activas"
                    value={loading ? "..." : stats.properties}
                    trend="up"
                    trendValue="0"
                    icon={Building2}
                    color="#3b82f6"
                    onClick={() => navigate('/properties')}
                />
                <StatCard
                    title="Leads Registrados"
                    value={loading ? "..." : stats.leads}
                    trend="up"
                    trendValue="0%"
                    icon={UsersIcon}
                    color="#10b981"
                    onClick={() => navigate('/leads')}
                />
                <StatCard
                    title="Tiempo Respuesta"
                    value={stats.responseTime}
                    trend="down"
                    trendValue="5m"
                    icon={Clock}
                    color="#f59e0b"
                    isClickable={false}
                />
                <StatCard
                    title="Ingresos Estimados"
                    value={loading ? "..." : `$${(stats.inventory_value || 0).toLocaleString()}`}
                    trend="up"
                    trendValue="0%"
                    icon={DollarSign}
                    color="#6366f1"
                    isClickable={false}
                />
            </div>

            {/* Middle Grid: Calendar, Timeline, Todo */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="h-[450px]">
                    <VisitCalendar />
                </div>
                <div className="h-[450px]">
                    <DetailedTimeline />
                </div>
                <div className="h-[450px] relative isolate">
                    <TaskActionList />
                </div>
            </div>

            {/* Bottom Grid: Pro Distribution & Knowledge */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                    <ProDistributionChart />
                </div>
                <div className="lg:col-span-2">
                    <Card className="h-full border-0 bg-slate-900 text-white rounded-2xl overflow-hidden shadow-2xl relative group">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl -mr-32 -mt-32"></div>
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-500/10 rounded-full blur-3xl -ml-24 -mb-24"></div>

                        <CardHeader className="relative z-10">
                            <CardTitle className="flex items-center gap-3 text-2xl font-black">
                                <BookOpen className="text-blue-400 w-8 h-8" />
                                Centro de Conocimiento Futura
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="relative z-10 flex flex-col justify-center h-full pb-10">
                            <h3 className="text-3xl font-black mb-4 leading-tight">Masterclass: Cierres de Alto Impacto en 2026</h3>
                            <p className="text-slate-400 text-lg mb-8 max-w-2xl leading-relaxed">
                                Aprende las tácticas psicológicas y herramientas de IA que están usando los top producers de Servicios Futura para cerrar propiedades de lujo en tiempo récord.
                            </p>
                            <div className="flex gap-4">
                                <Button className="bg-blue-600 hover:bg-blue-500 text-white font-black rounded-xl h-12 px-8 shadow-xl shadow-blue-500/20" onClick={() => navigate('/knowledge')}>
                                    Acceso Exclusivo Agentes
                                </Button>
                                <Button variant="ghost" className="text-white hover:bg-white/10 font-bold rounded-xl h-12 px-6">
                                    Ver todas las guías
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div >
    );
}

